import { APIResponse } from '../../../lib/commons/api'
import { isPermissionError } from '../../../handlers/globalErrorHandler'

/**
 * 再取得しても結論が変わらない失敗か。true なら失敗をキャッシュしてよい。
 *
 * 参照権限なし（`UNAUTHORIZED` / `UNAUTHORIZED_SCOPE`）と 404（存在しない）が該当する。
 * それ以外（サーバエラー・ネットワーク断など）は一時的な失敗として扱い、キャッシュしない。
 */
export const isPermanentLookupFailure = (error: unknown): boolean => {
  if (isPermissionError(error)) {
    return true
  }
  return (error as { status?: number } | null)?.status === 404
}

export type BasicByCodeCache<T> = {
  // 同期的にキャッシュだけを覗く。initial state 用。
  // undefined: 未取得（呼び出し側が load を呼ぶ必要がある）
  // null: 取得済みだが利用不可（存在しない or 参照権限なし）
  // それ以外: 取得済みの実体
  peek: (code: string) => T | null | undefined
  load: (code: string) => Promise<T | null>
}

/**
 * 応答本文のチップが「コードから実体を引く」ための共通キャッシュを作る。
 *
 * 応答本文では同じ WBS アイテム・同じユーザーへの言及が複数回・複数メッセージにまたがって
 * 出現しうる（例: 同じチケットを繰り返し参照しながら説明する / 担当者を何度も挙げる）。
 * チップは本文中に独立していくつも並ぶコンポーネントだが、そのためだけに Context/Redux を
 * 新設するほどではないため、モジュールスコープの単純なキャッシュで「同じコードは1回しか
 * 取得しない」を保証する（CLAUDE.md §7: 画面内はおろか他画面にも関係しない、取得結果の
 * 使い回しだけの用途）。タブを閉じる・リロードすると消えるが、頻繁に変わる値ではないため
 * 再取得のコストは小さい。
 *
 * 取得に失敗したときの表示は理由を問わず同じ（チップをやめて本文のプレーンテキストにする）だが、
 * 失敗を **キャッシュするかどうか** は理由によって変える。404（存在しないコード = モデルの幻覚）と
 * 403（参照権限なし）は何度引いても結論が変わらないのでキャッシュして再取得しない。一方
 * 500 やネットワーク断は一時的な失敗であり、キャッシュに残すとページをリロードするまで二度と
 * 表示できなくなるため、キャッシュせず次の取得機会にやり直せるようにする。
 *
 * @param fetchByCode コードから実体を取得する API 呼び出し
 * @param subject 取得に失敗したときのログに出す対象の名前（原因の切り分け用）
 */
export const createBasicByCodeCache = <T>(
  fetchByCode: (code: string) => Promise<APIResponse>,
  subject: string
): BasicByCodeCache<T> => {
  const cache = new Map<string, T | null>()
  const inFlight = new Map<string, Promise<T | null>>()

  const load = (code: string): Promise<T | null> => {
    if (cache.has(code)) {
      return Promise.resolve(cache.get(code) ?? null)
    }
    const pending = inFlight.get(code)
    if (pending) {
      return pending
    }
    const request = fetchByCode(code)
      .then(response => {
        const basic = response.json as T
        cache.set(code, basic)
        return basic
      })
      .catch(error => {
        // 本文中の一要素が引けなかっただけであり、会話全体を止めたり全画面トーストを出したり
        // する話ではない。globalErrorHandler は unhandledrejection を拾って全画面トーストを
        // 出す作りのため、ここで確実に catch して伝播を止める（再throwしない）。原因の切り分けが
        // できるよう console にだけ残す。
        console.warn(`Failed to fetch ${subject} for a chat link chip.`, {
          code,
          error,
        })
        if (isPermanentLookupFailure(error)) {
          cache.set(code, null)
        }
        return null
      })
      .finally(() => {
        inFlight.delete(code)
      })
    inFlight.set(code, request)
    return request
  }

  return {
    peek: (code: string) => cache.get(code),
    load,
  }
}
